import { Injectable, signal } from '@angular/core';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  // Red icon/confirm button for destructive actions (delete, remove, …) vs. the default neutral
  // blue for a plain yes/no question.
  danger?: boolean;
  // Font Awesome icon class overriding the danger/question default — e.g. 'fa-sign-out-alt' for
  // a logout confirm, which is styled as danger (red) but isn't actually a delete/trash action.
  icon?: string;
}

const DEFAULTS: Partial<ConfirmOptions> = { confirmText: 'Confirm', cancelText: 'Cancel', danger: false };

// A single shared confirm dialog (mounted once, in app.html) instead of the browser's own
// window.confirm() — that one can't be styled, blocks the whole tab's JS thread while open, and
// looks jarringly out of place next to the rest of the app's UI.
@Injectable({ providedIn: 'root' })
export class ConfirmService {
  options = signal<ConfirmOptions>({ message: '' });
  private resolveFn: ((result: boolean) => void) | null = null;

  confirm(options: ConfirmOptions): Promise<boolean> {
    this.options.set({ ...DEFAULTS, ...options });
    return new Promise((resolve) => {
      this.resolveFn = resolve;
      const el = document.getElementById('sharedConfirmModal');
      if (el) {
        // Re-parent to the end of <body> on every open, not just once at app startup. Page modals
        // (machineModal, stepsModal-*, …) each move themselves to the end of <body> via
        // appAppendToBody when THEY open, so whichever modal did that most recently ends up on top
        // when both share the same z-index — without this, a page modal opened before this dialog
        // permanently paints over it.
        document.body.appendChild(el);
        el.style.zIndex = '1060';

        // Always manage the backdrop ourselves (backdrop: false) instead of letting Bootstrap
        // create one per modal. When this dialog opens on top of an already-open page modal (e.g.
        // deleting a step from inside the Steps modal), two stacked semi-transparent
        // .modal-backdrop layers can leave faint seam lines showing through high-contrast content
        // (table borders, etc.) behind them. Reusing that modal's existing backdrop — just bumping
        // it above its own dialog — dims it properly with a single layer instead.
        const bootstrapModal = (window as any).bootstrap.Modal.getOrCreateInstance(el, { backdrop: false });
        bootstrapModal.show();

        let backdrop = document.querySelector<HTMLElement>('.modal-backdrop');
        if (!backdrop) {
          backdrop = document.createElement('div');
          backdrop.className = 'modal-backdrop fade show';
          backdrop.id = 'confirmDialogBackdrop';
          document.body.appendChild(backdrop);
        }
        backdrop.style.zIndex = '1055';
      }
    });
  }

  // Called by the dialog's own buttons, and as a fallback when the modal is dismissed any other
  // way (Escape, backdrop click) — that always means "cancelled", never a silent hang.
  resolve(result: boolean) {
    if (!this.resolveFn) return;
    const ownBackdrop = document.getElementById('confirmDialogBackdrop');
    if (ownBackdrop) {
      ownBackdrop.remove();
    } else {
      // Reused another modal's backdrop instead of creating our own — hand it back at its normal
      // z-index rather than removing it, since that modal is still open underneath.
      document.querySelector<HTMLElement>('.modal-backdrop')?.style.removeProperty('z-index');
    }
    this.resolveFn(result);
    this.resolveFn = null;
  }
}
